// ============================================================
// TMR Portal Admin — Detalhe da empresa: visão geral do cadastro
// ============================================================
import { useState } from 'react';
import { StatusBadge } from '../../components/primitives';
import { Field } from '../../components/ui';
import { PersonChip, PersonPeekModal } from '../../components/party';
import type { PeekPerson } from '../../components/party';
import { companyAddress, maskCEP, maskCNPJ, maskCPF } from '../../lib/format';
import type { CompanyRecord, PartyReference } from '../../data/companies/contract';
import { classificationLabel } from '../../data/companies/queries';
import { segmentLabel } from '../../data/companies/segment';
import { Notice } from '../../components/Notice';
import { blocksExport, gapSummary } from '../../data/companies/gaps';

/**
 * What a person reads as when the Parties directory no longer resolves the id the Empresa stores.
 *
 * Since B8.4 the Empresa keeps only the reference, and the details are the directory's answer at read
 * time — so they arrive null when the Party was archived or removed. Saying so is the point: an Empresa
 * whose representative cannot be resolved will not export (their e-mail is what matches the producer
 * login in the legacy system), and a blank field would read as an Empresa that simply has none.
 */
const UNRESOLVED_PARTY = 'Pessoa não encontrada no cadastro';

/** Props for {@link CompanyOverview}. */
interface CompanyOverviewProps {
  /** The Empresa's record, as the backend sent it. */
  company: CompanyRecord;
}

/** The Empresa's registration, read from the backend. Editing lands with the update slice. */
export function CompanyOverview({ company }: CompanyOverviewProps) {
  const [peek, setPeek] = useState<PeekPerson | null>(null);
  const address = companyAddress(company.address);

  const openPeek = (person: PartyReference, subtitle: string) =>
    setPeek({
      nome: person.name ?? UNRESOLVED_PARTY,
      cpf: person.cpf ? maskCPF(person.cpf) : undefined,
      email: person.email ?? undefined,
      telefone: person.phone ?? undefined,
      subtitle,
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* First thing on the tab, deliberately: after the legacy backfill this is the reason the operator
          opened the record, and it is what stops the Empresa replicating (19 §5). Only the blocking gaps
          say so — a missing razão social is worth showing and does not hold up the export. */}
      {company.registrationGaps.length > 0 && (
        <Notice tone={blocksExport(company.registrationGaps) ? 'warning' : 'neutral'}>
          <strong>Cadastro incompleto:</strong> falta {gapSummary(company.registrationGaps)}.{' '}
          {blocksExport(company.registrationGaps)
            ? 'Enquanto isso, esta empresa não é replicada para o sistema legado — complete os campos e salve.'
            : 'Não impede a replicação, mas vale completar.'}
        </Notice>
      )}

      <div className="pa-cards-masonry">
        <div className="pa-card" style={{ padding: 20 }}>
          <div className="pa-eyebrow" style={{ marginBottom: 6 }}>Empresa</div>
          <Field label="Nome fantasia">{company.name}</Field>
          <Field label="Razão social">{company.legalName || '—'}</Field>
          <Field label="CNPJ">{company.cnpj ? maskCNPJ(company.cnpj) : '—'}</Field>
          <Field label="Domínio">{company.subdomain}</Field>
          <Field label="Origem">{company.acquisitionOrigin || '—'}</Field>
          {/* The backend resolves the label against the catalogue, so nothing here has to fetch it. An
              id the catalogue no longer offers arrives with no code and says so, rather than showing a
              bare number or silently reading as "never classified". */}
          <Field label="Classificação">
            {company.classification ? classificationLabel(company.classification) : '—'}
          </Field>
          <Field label="Segmento">{company.segment ? segmentLabel(company.segment) : '—'}</Field>
          <Field label="Gestor de venda">{company.salesManager?.name || '—'}</Field>
          <Field label="Gestor de pós-venda">{company.postSalesManager?.name || '—'}</Field>
          <Field label="Replicação ao legado">
            <StatusBadge status={company.exportStatus} />
          </Field>
          <Field label="Cobrança especial">{company.specialBilling ? 'Sim' : 'Não'}</Field>
          {company.description && <Field label="Descrição">{company.description}</Field>}
        </div>

        <div className="pa-card" style={{ padding: 20 }}>
          <div className="pa-eyebrow" style={{ marginBottom: 6 }}>Endereço</div>
          <Field label="Endereço">
            {company.address.street}, {company.address.number}
            {company.address.complement ? ` – ${company.address.complement}` : ''}
          </Field>
          <Field label="Bairro / Cidade">
            {company.address.district} · {company.address.city}/{company.address.state}
          </Field>
          <Field label="CEP">{maskCEP(company.address.zipCode)}</Field>
          {company.address.country !== 'Brasil' && <Field label="País">{company.address.country}</Field>}
          {address.precision === 'none' && <Field label="Mapa">Endereço insuficiente para localizar.</Field>}
        </div>

        <div className="pa-card" style={{ padding: 20 }}>
          <div className="pa-eyebrow" style={{ marginBottom: 6 }}>Contato & representante</div>
          <Field label="Contato focal">
            {company.focalContact ? (
              <PersonChip
                name={company.focalContact.name ?? UNRESOLVED_PARTY}
                onClick={() => openPeek(company.focalContact!, 'Contato focal')}
              />
            ) : (
              '—'
            )}
          </Field>
          <Field label="Representante legal">
            {/* Nobody on file is a different state from a reference the directory cannot resolve, and the
                two read differently on purpose: one is a gap to fill, the other a person who vanished. */}
            {company.representative ? (
              <PersonChip
                name={company.representative.name ?? UNRESOLVED_PARTY}
                sub={company.representative.email ?? undefined}
                onClick={() => openPeek(company.representative!, 'Representante legal')}
              />
            ) : (
              <span style={{ color: 'var(--fg-subtle)' }}>Não cadastrado</span>
            )}
          </Field>
        </div>

      </div>

      {peek && <PersonPeekModal person={peek} onClose={() => setPeek(null)} />}
    </div>
  );
}
